import React, { forwardRef, useImperativeHandle, useRef } from 'react';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import { Divider, Stack, TextField, useMediaQuery, useTheme } from '@mui/material';
import { useStyles } from '../style';

/**
 * @name AccountDetails - Account details component
 * @param {string} errors - Errors returned on input field validation
 * @function onChange  - Event handling function on field input
 * @param {object} ref - References to forward account details field
 * @returns {JSX.Element} - Body of the Action Modal
 */
const AccountDetails = forwardRef(({ errors, onChange, values, onBlur }, ref) => {
  const theme = useTheme();
  const style = useStyles(theme);
  const sm = useMediaQuery(theme.breakpoints.down('sm'));
  const accountNumberRef = useRef(null);
  const landLineNumberRef = useRef(null);

  /**
   * @name useImperativeHandle
   * @description - customizes the instance value i.e. exposed to parent components when using ref
   * @param {string} ref - References text fileds
   * @returns {string} -Returns ref value
   */
  useImperativeHandle(
    ref,
    () => ({
      /**
       * @name focusAccountNumber
       * @description Functiomn to focus account Number field
       * @returns {Function} - Focuses current ref value
       */
      focusAccountNumber: () => accountNumberRef.current?.focus(),
      /**
       * @name focusLandLineNumber
       * @description Functiomn to focus landline Number field
       * @returns {Function} - Focuses current ref value
       */
      focusLandLineNumber: () => landLineNumberRef.current?.focus(),
    }),
    [],
  );

  return (
    <Box>
      {sm && <Divider sx={style.divider} />}
      <Stack spacing={4} sx={style.fields}>
        <FormControl variant='outlined' error={!!errors?.accountNumber}>
          <TextField
            name='accountNumber'
            label='Account Number'
            variant='outlined'
            sx={style.inputTextbox}
            inputRef={accountNumberRef}
            onChange={onChange}
            onBlur={onBlur}
            helperText={errors.accountNumber}
            value={values.accountNumber}
          />
        </FormControl>
        <FormControl variant='outlined' error={!!errors?.landLineNumber}>
          <TextField
            name='landLineNumber'
            label='Last 4 digits mobile/landline number'
            variant='outlined'
            sx={style.inputTextbox}
            inputRef={landLineNumberRef}
            onChange={onChange}
            onBlur={onBlur}
            helperText={errors.landLineNumber}
            value={values.landLineNumber}
          />
        </FormControl>
      </Stack>
    </Box>
  );
});

AccountDetails.displayName = 'AccountDetails';
export default AccountDetails;
